// components/SearchAndTopicBar.jsx
import React from 'react';

const SearchAndTopicBar = ({
  itemId,
  searchValue,
  onSearchChange,
  topics,
  selectedTopicId,
  onTopicFilterChange,
  lang = 'en',
}) => {
  const handleSearchInputChange = (e) => {
    onSearchChange?.(e.target.value);
  };

  const handleSearchClear = (e) => {
    e.preventDefault();
    onSearchChange?.('');
  };

  const handleTopicChange = (e) => {
    onTopicFilterChange?.(e.target.value);
  };

  // Pin "General Talks and Activities" to the top of topic options.
  // Keep all other topics in their incoming order.
  const sortedTopics = [...topics].sort((a, b) => {
    const aIsGeneral =
      String(a?.name || '').trim().toLowerCase() === 'general talks and activities';
    const bIsGeneral =
      String(b?.name || '').trim().toLowerCase() === 'general talks and activities';

    if (aIsGeneral && !bIsGeneral) return -1;
    if (!aIsGeneral && bIsGeneral) return 1;
    return 0;
  });

  const selectedTopicLabel =
    selectedTopicId === 'all'
      ? lang === 'cy'
        ? 'Hidlo canlyniadau'
        : 'Filter results'
      : sortedTopics.find((topic) => String(topic.id) === String(selectedTopicId))?.name ||
        (lang === 'cy' ? 'Hidlo canlyniadau' : 'Filter results');

  return (
    <div className="search-form with-radio-group">
      <form className="form" onSubmit={(e) => e.preventDefault()}>
        <div className="d-flex flex-column flex-lg-row">
          {/* SEARCH FORM CONTAINER - Contains both search input and dropdown */}
          <div className="search-form-container order-1 order-lg-1 mb-3 mb-lg-0">
            <div className="search-form-query-container">
              <label
                className="search-form-label"
                htmlFor={`query-caru-${itemId}`}
              >
                <span className="sr-only">Search</span>
                <svg
                  className="icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                >
                  <title>Search</title>
                  <path d="M31.72,28.94l-9.62-9.66c-0.06,0-.09,0-0.09-0.06l-0.09-.06a1.54,1.54,0,0,0-.22-0.09,11.92,11.92,0,0,0,1.72-3.31A12.25,12.25,0,0,0,24,12,12,12,0,0,0,12,0,11.95,11.95,0,0,0,0,12a11.82,11.82,0,0,0,3.5,8.47A11.9,11.9,0,0,0,12,24a11.56,11.56,0,0,0,3.78-.62,11.85,11.85,0,0,0,3.31-1.69,0.12,0.12,0,0,0,0,.09l0.06,0.09a0.16,0.16,0,0,0,.06.09,0.11,0.11,0,0,1,0,.09L29,31.72a1.14,1.14,0,0,0,.72.28,1,1,0,0,0,.66-0.28l1.41-1.37A1.47,1.47,0,0,0,32,29.66a1.14,1.14,0,0,0-.31-0.72h0ZM12,4a8,8,0,0,1,8,8,7.88,7.88,0,0,1-2.31,5.66A8,8,0,0,1,12,20a7.85,7.85,0,0,1-5.66-2.34,7.92,7.92,0,0,1,0-11.31A7.85,7.85,0,0,1,12,4Z" />
                </svg>
              </label>

              <input
                type="search"
                className="search-form-query"
                id={`query-caru-${itemId}`}
                name="query"
                placeholder={lang === 'cy' ? 'Rhowch allweddair' : 'Enter keyword'}
                value={searchValue}
                onChange={handleSearchInputChange}
              />

              <a
                href="#"
                className="search-form-clear"
                aria-label="Clear search query"
                style={{
                  display:
                    searchValue && searchValue.length > 0
                      ? 'inline-flex'
                      : 'none',
                }}
                onClick={handleSearchClear}
              >
                <span aria-hidden="true">×</span>
              </a>
            </div>

            {/* TOPIC FILTER DROPDOWN - Inside search-form-container for connected look */}
            <div className="radio-btn-group d-none d-lg-block">
              <label htmlFor={`event-topic-${itemId}`} className="sr-only">
                {lang === 'cy' ? 'Hidlo canlyniadau' : 'Filter results'}
              </label>
              <div style={{ margin: '-0.25rem' }}>
                <select
                  id={`event-topic-${itemId}`}
                  name={`event-topic-${itemId}`}
                  className="form-control dropdown dropdown--clearable dropdown-no-border border-0 topic-dropdown-ellipsis"
                  data-close-on-selection="true"
                  value={selectedTopicId}
                  onChange={handleTopicChange}
                  title={selectedTopicLabel}
                >
                  <option value="all">
                    {lang === 'cy' ? 'Hidlo canlyniadau' : 'Filter results'}
                  </option>
                  {/* Ordered so "General Talks and Activities" appears first. */}
                  {sortedTopics.map((topic) => (
                    <option key={topic.id} value={String(topic.id)}>
                      {topic.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* MOBILE: Separate dropdown below search */}
          <div className="form order-2 order-lg-2 d-lg-none">
            <label htmlFor={`event-topic-mobile-${itemId}`} className="sr-only">
              {lang === 'cy' ? 'Hidlo canlyniadau' : 'Filter results'}
            </label>
            <div style={{ margin: '-0.25rem' }}>
              <select
                id={`event-topic-mobile-${itemId}`}
                name={`event-topic-mobile-${itemId}`}
                className="form-control dropdown dropdown--clearable topic-dropdown-ellipsis"
                data-close-on-selection="true"
                value={selectedTopicId}
                onChange={handleTopicChange}
                title={selectedTopicLabel}
              >
                <option value="all">
                  {lang === 'cy' ? 'Hidlo canlyniadau' : 'Filter results'}
                </option>
                {/* Ordered so "General Talks and Activities" appears first. */}
                {sortedTopics.map((topic) => (
                  <option key={topic.id} value={String(topic.id)}>
                    {topic.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchAndTopicBar;
